import React, { useEffect, useState } from 'react'
import { useSpring } from 'framer-motion'
import CreateNewCampaignStepOne from './CreateNewCampaignStepOne'
import CreateNewCampaignFilters from './CreateNewCampaignFilters'
import { atom, PrimitiveAtom, useAtom } from 'jotai'
import CreateNewCampaignPlatforms from './CreateNewCampaignProducts'
import CreateNewCampaignDetails from './CreateNewCampaignDetails'
import CreateNewCampaignReview from './CreateNewCampaignReview'
import AddNewCampaignSuccess from './AddNewCampaignSuccess'
import CreateNonInventoryCampaignObjectives from '../../NonInventoryCampaign/NewCampaign/CreateNonInventoryCampaignObjectives'
import CreateNonInventoryCampaignProducts from '../../NonInventoryCampaign/NewCampaign/CreateNonInventoryCampaignProducts'
import CreateNonInventoryCampaignReview from '../../NonInventoryCampaign/NewCampaign/CreateNonInventoryCampaignReview'
import CreateNonInventoryCampaignLaunchPage from '../../NonInventoryCampaign/NewCampaign/CreateNonInventoryCampaignLaunchPage'
import Modal from '../../../components/Modal/Modal'
import { FormProvider, useForm } from 'react-hook-form'

export const createCampaignFormValuesAtom: PrimitiveAtom<{
  campaign_type?: 'inventory' | 'non_inventory' | undefined
  campaign_name?: string
  campaign_id?: number
  pacing_id?: number
  notes?: string
  start_date?: string
  end_date?: string
  budget?: string
  location?: string
  target?: {
    zip?: string
    use_custom_audience?: boolean
    audience: Array<Record<string, string | number>>
  }
  objective?: string
  kpi_goals?: {
    primaryKpi?: string
    secondaryKpi?: string
    tertiaryKpi?: string
  }
  inventory?: {
    details: {
      client_id?: number
      client_name?: string
      inventory_campaign_name?: string
      inventory_data_provider_id?: string
      inventory_data_provider_client_id?: string
      inventory_data_providers?: string
      inventory_data_provider_clients?: string
      destination_url?: string
      is_active?: string
      no_tracking?: string
      click_cap?: number
      allowed_vehicle_state_ids?: string
      allowed_vehicle_states?: string
    }
    filters?: {
      allowed_makes?: string
      disallowed_makes?: string
      allowed_models?: string
      disallowed_models?: string
      allowed_body_substrings?: string
      disallowed_body_substrings?: string
      earliest_allowed_year?: string
      latest_allowed_year?: string
      lowest_allowed_price?: string
      highest_allowed_price?: string
      lowest_allowed_mileage?: string
      highest_allowed_mileage?: string
      allowed_stock_number_prefixes?: string
      disallowed_stock_number_prefixes?: string
      minimum_age_days?: string
      maximum_age_days?: string
      allowed_locations?: string
      disallowed_locations?: string
      no_price_only?: boolean
      exclude_if_no_price?: boolean
    }
  }
  product?: {
    product_name: string
    product_code: string
    product_description: string
    icon?: JSX.Element
    creatives?: string[]
  }
}> = atom<{
  campaign_type?: 'inventory' | 'non_inventory' | undefined
}>({
  campaign_type: undefined,
})

const CreateNewCampaignForm = (): JSX.Element => {
  const stepAnimation = useSpring(1, {})
  const [step, setStep] = useState(1)
  const [formValues] = useAtom(createCampaignFormValuesAtom)
  const { campaign_type } = formValues
  const maxSteps = 6
  const formContext = useForm({
    mode: 'onSubmit',
  })

  const getFormStepBasedOnCampaignType = (): JSX.Element | undefined => {
    switch (campaign_type) {
      case 'inventory':
        switch (step) {
          case 1:
            return <CreateNewCampaignStepOne step={step} setStep={setStep} />
          case 2:
            return <CreateNewCampaignPlatforms step={step} setStep={setStep} />
          case 3:
            return <CreateNewCampaignFilters step={step} setStep={setStep} />
          case 4:
            return <CreateNewCampaignDetails step={step} setStep={setStep} />
          case 5:
            return <CreateNewCampaignReview step={step} setStep={setStep} />
          case 6:
            return <AddNewCampaignSuccess />
        }
        break
      case 'non_inventory':
        switch (step) {
          case 1:
            return <CreateNewCampaignStepOne step={step} setStep={setStep} />
          case 2:
            return <CreateNonInventoryCampaignProducts step={step} setStep={setStep} />
          case 3:
            return <CreateNonInventoryCampaignObjectives step={step} setStep={setStep} />
          case 4:
            return <CreateNewCampaignDetails step={step} setStep={setStep} />
          case 5:
            return <CreateNonInventoryCampaignReview step={step} setStep={setStep} />
          case 6:
            return <CreateNonInventoryCampaignLaunchPage />
        }
        break
      default:
        return <CreateNewCampaignStepOne step={step} setStep={setStep} />
    }
  }

  const getStepTitle = (): string => {
    switch (campaign_type) {
      case 'inventory':
        switch (step) {
          case 1:
            return '1. What type of campaign do you want to build?'
          case 2:
            return '2. How do you want to promote?'
          case 3:
            return '3. What cars do you want to promote?'
          case 4:
            return '4. Campaign details'
          case 5:
            return 'Review'
          case 6:
            return ''
          default:
            return '1. Campaign type'
        }
        break
      case 'non_inventory':
        switch (step) {
          case 2:
            return '2. Product'
          case 3:
            return '3. Objective'
          case 4:
            return '4. Details'
          case 5:
            return `Review - ${formValues.campaign_name}`
          case 6:
            return ''
          default:
            return '1. What type of campaign do you want to build?'
        }
        break
      default:
        return '1. Campaign type'
    }
  }

  const getStepSubtitle = (): string => {
    switch (campaign_type) {
      case 'inventory':
        return ''
      case 'non_inventory':
        switch (step) {
          case 2:
            return 'Choose the method of reaching your target audience'
          case 3:
            return 'What are you trying to achieve with this campaign?'
          case 4:
            return "Specific and accurate representation of your the customer you're trying to reach."
          case 5:
            return 'You can revise all your steps here before launching'
          default:
            return ''
        }
      default:
        return ''
    }
  }

  useEffect(() => {
    if (step <= maxSteps) stepAnimation.set(step / maxSteps)
  }, [maxSteps, step, stepAnimation])

  return (
    <FormProvider {...formContext}>
      <div className={'flex flex-1 h-full flex-col overflow-y-scroll'}>
        <div className={'flex flex-col flex-1'}>
          <div className={'flex flex-col gap-3 px-12 pt-12 '}>
            <div className={'font-bold text-[28px] text-primary-xiketic'}>
              {getStepTitle()}
            </div>
            <div className={'text-sm'}>{getStepSubtitle()}</div>
          </div>
          {getFormStepBasedOnCampaignType()}
        </div>
      </div>
    </FormProvider>
  )
}

export default CreateNewCampaignForm
